"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.yyyyMMParser = exports.parseIntOrThrow = exports.parseFloatSafe = exports.parseIntSafeOrZero = exports.parseIntSafeOr = exports.parseIntSafe = exports.iso8601DateParser = exports.rfc3339TimestampParser = exports.iso8601TimestampParser = exports.unixTimestampParser = exports.javascriptTimestampParser = void 0;
exports.isValidHttpUrl = isValidHttpUrl;
const luxon_1 = require("luxon");
const javascriptTimestampParser = (value) => {
    if (Math.abs(value) >= 1_000_000_000_000_000) {
        throw new Error(`Provided JavaScript timestamp '${value}' is not valid.`);
    }
    const date = new Date(value);
    if (date == null || isNaN(date.getTime())) {
        throw new Error(`Invalid date '${value}'.`);
    }
    if (Math.abs(date.getTime()) >= 1_000_000_000_000_000) {
        throw new Error(`Parsed date '${date.toISOString()}' is not valid.`);
    }
    return date;
};
exports.javascriptTimestampParser = javascriptTimestampParser;
const unixTimestampParser = (value) => {
    if (Math.abs(value) >= 1_000_000_000_000) {
        throw new Error(`Provided UNIX timestamp '${value}' is not valid.`);
    }
    const date = new Date(value * 1000);
    if (date == null || isNaN(date.getTime())) {
        throw new Error(`Invalid date '${value}'.`);
    }
    if (Math.abs(date.getTime()) >= 1_000_000_000_000_000) {
        throw new Error(`Parsed date '${date.toISOString()}' is not valid.`);
    }
    return date;
};
exports.unixTimestampParser = unixTimestampParser;
const iso8601TimestampParser = (value) => {
    const isoDateRegex = new RegExp(/^\d{4}-(0[1-9]|1[0-2])-([0-2]\d|3[01])T([01]\d|2[0-3]):([0-5]\d):([0-5]\d)(.\d+)?(Z|[+-]\d{2}(:[0-5]\d)?)$/gm);
    if (!isoDateRegex.test(value)) {
        throw new Error(`Provided ISO 8601 timestamp '${value}' is not valid. It should follow ISO 8601 timestamp format.`);
    }
    const date = luxon_1.DateTime.fromISO(value).toJSDate();
    if (date == null || isNaN(date.getTime())) {
        throw new Error(`Invalid date '${value}'.`);
    }
    if (Math.abs(date.getTime()) >= 1_000_000_000_000_000) {
        throw new Error(`Parsed date '${date.toISOString()}' is not valid. It is too far away in time.`);
    }
    return date;
};
exports.iso8601TimestampParser = iso8601TimestampParser;
const rfc3339TimestampParser = (value) => {
    const rfc3339Regex = new RegExp(/^(?:(?<year>19\d{2}|2[0-1]\d{2})-(?<month>0[1-9]|1[0-2])-(?<day>0[1-9]|[12]\d|3[01])(?:[ _Tt])?)?(?:(?<hour>[01]\d|2[0-3]):(?<minute>[0-5]\d):(?<second>[0-5]\d)(?<fractionalSecond>\.\d{1,6})?)?(?:(?<timezone>z|Z|[+-]\d{2}:\d{2}))?$/m);
    const matches = rfc3339Regex.exec(value);
    if (!matches || !matches.groups) {
        throw new Error(`Provided RFC 3339 timestamp '${value}' is not valid. It should follow RFC 3339 timestamp format.`);
    }
    const { year = "1970", month = "01", day = "01", hour = "00", minute = "00", second = "00", fractionalSecond, timezone = "Z", } = matches.groups;
    const iso8601String = `${year}-${month}-${day}T${hour}:${minute}:${second}${fractionalSecond || ""}${timezone}`;
    const date = new Date(iso8601String);
    if (date == null || isNaN(date.getTime())) {
        throw new Error(`Invalid date '${value}'.`);
    }
    if (Math.abs(date.getTime()) >= 1_000_000_000_000_000) {
        throw new Error(`Parsed date '${date.toISOString()}' is not valid. It is too far away in time.`);
    }
    return date;
};
exports.rfc3339TimestampParser = rfc3339TimestampParser;
const iso8601DateParser = (value) => {
    const date = (0, exports.iso8601TimestampParser)(value);
    if (date.getUTCHours() > 0 ||
        date.getUTCMinutes() > 0 ||
        date.getUTCSeconds() > 0 ||
        date.getUTCMilliseconds() > 0) {
        throw new Error(`Parsed date '${date.toISOString()}' has time. The date should be at time 00:00:00Z`);
    }
    return date;
};
exports.iso8601DateParser = iso8601DateParser;
const isInt = /^-?\d+$/;
const parseIntSafe = (num) => {
    if (!isInt.test(num)) {
        return NaN;
    }
    const r = Number(num);
    return Number.isSafeInteger(r)
        ? r
        : NaN;
};
exports.parseIntSafe = parseIntSafe;
const parseIntSafeOr = (num, fallback) => {
    const parsed = (0, exports.parseIntSafe)(num);
    return isNaN(parsed) ? fallback : parsed;
};
exports.parseIntSafeOr = parseIntSafeOr;
const parseIntSafeOrZero = (num) => {
    return (0, exports.parseIntSafeOr)(num, 0);
};
exports.parseIntSafeOrZero = parseIntSafeOrZero;
const isWhitespacesOrEmpty = /^\s*$/;
const isFloatWithMaxDecimals = (maxNumDecimals) => {
    const integerPart = /-?\d+/;
    const decimalPart = maxNumDecimals === null
        ? "(\\.\\d+)?"
        : maxNumDecimals > 0
            ? `(\\.\\d{1,${maxNumDecimals}})?`
            : "";
    return new RegExp(`^${integerPart.source}${decimalPart}$`);
};
const parseFloatSafe = (num, maxNumDecimals = null) => {
    if (isWhitespacesOrEmpty.test(num)) {
        return NaN;
    }
    const floatRegex = isFloatWithMaxDecimals(maxNumDecimals);
    if (!floatRegex.test(num)) {
        return NaN;
    }
    return Number(num);
};
exports.parseFloatSafe = parseFloatSafe;
const parseIntOrThrow = (num) => {
    const n = (0, exports.parseIntSafe)(num);
    if (isNaN(n)) {
        throw new Error(`Error trying to parse ${num} to int`);
    }
    return n;
};
exports.parseIntOrThrow = parseIntOrThrow;
function isValidHttpUrl(string) {
    let url;
    try {
        url = new URL(string);
    }
    catch (_) {
        return false;
    }
    return url.protocol === "http:" || url.protocol === "https:";
}
const yyyyMMParser = (value) => {
    const yyyyMmRegex = new RegExp(/^(?<year>2\d{3}|19\d{2})-(?<month>0[1-9]|1[0-2])$/);
    const matches = yyyyMmRegex.exec(value);
    if (!matches || !matches.groups) {
        throw new Error(`Invalid date provided '${value}'`);
    }
    const { year: yearStr, month: monthStr } = matches.groups;
    const [year, month] = [Number(yearStr), Number(monthStr)];
    if (!yearStr || !monthStr || year == null || month == null || isNaN(year) || isNaN(month)) {
        throw new Error(`Invalid date provided '${value}'`);
    }
    const date = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0));
    if (isNaN(date.getTime())) {
        throw new Error(`Invalid date provided '${value}'`);
    }
    return date;
};
exports.yyyyMMParser = yyyyMMParser;
//# sourceMappingURL=parsers.js.map